import React, { useEffect, useState } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import {
  ListStacksResponseItem,
  StackStateEnumDto,
  OrganizationDepartmentResponse,
} from '.generated/api';

import {
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  RadioGroup,
  Radio,
  FormControlLabel,
  ListItemIcon,
  Divider,
  LabelDisplayedRowsArgs,
  Typography,
  Tooltip,
} from '@material-ui/core';

import { Delete, GetApp, FiberManualRecord, Refresh } from '@material-ui/icons';

import { primaryColor, theme } from 'theme/theme';
import { useTranslation } from 'react-i18next';
import 'translations/translations';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import { stackActions } from 'actions/stackActions';
import IconSpinner from 'components/spinners/ButtonSpinner';
import { sawmillActions } from 'actions/sawmillActions';
import OverlaySpinner from 'components/spinners/OverlaySpinner';
import { notificationActions } from 'actions/notificationActions';
import SawmillIcon from 'icons/SawmillIcon';
import { ApiService } from 'services/apiService';

const useStyles = makeStyles({
  tableLayout: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    flex: 1,
  },
  container: {
    overflow: 'auto',
    flex: 1,
  },
  pagination: {
    flexShrink: 0,
    borderTop: '1px solid rgba(224, 224, 224, 1)',
    backgroundColor: 'rgba(255, 255, 255, 0.98)',
  },
  bulkToolbar: {
    flexShrink: 0,
    borderTop: '1px solid rgba(224, 224, 224, 1)',
    backgroundColor: 'rgba(240, 241, 243, 1)',
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    padding: '0 1rem',
    minHeight: '3rem',
  },
  bulkToolbarText: {
    flexGrow: 1,
    color: primaryColor(),
  },
  tableHeadCell: {
    backgroundColor: 'rgba(237, 240, 242, 1)', // transparent background is no good for sticky header
    fontWeight: 'bold',
    color: primaryColor(),
    padding: '0.7rem 1rem 0.5rem 1rem',
  },
  tableCell: {
    position: 'relative',
    '& > a': {
      color: 'inherit',
    },
    paddingTop: 0,
    paddingBottom: 0,
    color: primaryColor(),
  },
  tableRow: {
    textDecoration: 'none',
    cursor: 'pointer',
  },
  preDataView: {
    marginTop: '3rem',
    textAlign: 'center',
  },
  error: {
    color: theme.palette.error.main,
  },
  textBold: {
    fontWeight: 'bold',
    color: primaryColor(),
  },
  measuredIcon: {
    position: 'absolute',
    right: '100%',
    fontSize: '1rem',
    marginRight: '-0.5rem',
  },
  spinner: {
    height: '22px',
    width: '22px',
  },
  dialogContent: {
    height: 'auto',
    maxHeight: '33vh',
  },
  checkbox: {
    padding: 0,
  },
  radio: {
    margin: 0,
  },
});

interface ParamTypes {
  orgId: string;
}

function isMeasured(state: StackStateEnumDto): boolean {
  return (
    state === StackStateEnumDto.MEASURED ||
    state === StackStateEnumDto.ASSIGNED_TO_SAWMILL
  );
}

function isPreRegistration(state: StackStateEnumDto): boolean {
  return state === StackStateEnumDto.PRE_REGISTRATION;
}

function dateFromSelectedDateType(
  stack: ListStacksResponseItem,
  dateType: string,
) {
  switch (dateType) {
    case 'LastChanged': {
      if (stack.lastModifiedDateTime)
        return new Date(stack.lastModifiedDateTime).toLocaleDateString(
          navigator.language,
        );
      return '';
    }
    case 'PreRegistration': {
      if (stack.registrationDateTime)
        return new Date(stack.registrationDateTime).toLocaleDateString(
          navigator.language,
        );
      return '';
    }
    case 'Measured': {
      if (stack.measurementDateTime)
        return new Date(stack.measurementDateTime).toLocaleDateString(
          navigator.language,
        );
      return '';
    }
    case 'AssignedToSawmill': {
      if (stack.assignedToSawmillDateTime)
        return new Date(stack.assignedToSawmillDateTime).toLocaleDateString(
          navigator.language,
        );
      return '';
    }
    case 'Invoiceable': {
      if (stack.invoiceableDateTime)
        return new Date(stack.invoiceableDateTime).toLocaleDateString(
          navigator.language,
        );
      return '';
    }
    default:
      return '';
  }
}

interface StacksTableProps {
  onPageChanged?: (page: number) => void;
  onPageSizeChanged?: (page: number) => void;
}

function StacksTable({
  onPageChanged,
  onPageSizeChanged,
}: StacksTableProps): React.ReactElement {
  const classes = useStyles();
  const { t } = useTranslation(['translations']);
  const history = useHistory();
  const dispatch = useDispatch();
  const { orgId } = useParams<ParamTypes>();

  const [selectedStackIds, setSelectedStackIds] = useState<string[]>([]);
  const [isAssignDialogOpen, setAssignDialogOpen] = useState(false);
  const [isAssignDialogLoading, setAssignDialogLoading] = useState(false);
  const [isRegeneratingFiles, setIsRegeneratingFiles] = useState(false);
  const [selectedSawmill, setSelectedSawmill] = useState<string>('');
  const location = useLocation();
  const [dateType, setDateType] = useState<string>('Invoiceable');

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const dateType = query.get('dateType');
    if (dateType) setDateType(dateType);
  }, [location]);

  const [confirmStackDelete, setConfirmStackDelete] =
    useState<ListStacksResponseItem>();

  const { stacks, filters, sawmills, measurementCard } = useSelector(
    (state: ApplicationState) => ({
      stacks: state.stacks?.stacks,
      filters: state.stacks?.filters,
      sawmills: state.sawmills?.sawmills ?? [],
      measurementCard: state.stacks?.measurementCard,
    }),
  );

  const {
    stacksDeleting,
    archiveDownloadProgress,
    accumulatedCsvDownloadProgress,
  } = useSelector((state: ApplicationState) => ({
    stacksDeleting: state.stacks?.stacksDeleting,
    archiveDownloadProgress: state.stacks?.archiveDownloadProgress,
    accumulatedCsvDownloadProgress:
      state.stacks?.accumulatedCsvDownloadProgress,
  }));

  const isGlobalAdmin = useSelector(
    (state: ApplicationState) =>
      state.isLoggedIn?.userAccess?.isGlobalAdmin ?? false,
  );

  const departments = useSelector((state: ApplicationState) => {
    const deptMap: Record<string, OrganizationDepartmentResponse> = {};
    const departments = state.organization?.organization?.departments;
    if (departments) {
      for (const dept of departments) {
        deptMap[dept.departmentId] = dept;
      }
    }
    return deptMap;
  });

  // reset sawmill selection
  useEffect(() => {
    setSelectedSawmill(sawmills.length === 0 ? '' : sawmills[0].id);
  }, [sawmills]);

  // sawmill radio select
  const handleSawmillRadioChanged = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSelectedSawmill(e.target.value);
  };

  const handleAssignClicked = () => {
    if (selectedSawmill.length > 0 && selectedStackIds.length > 0) {
      setAssignDialogLoading(true);
      dispatch(
        sawmillActions.assignStacksToSawmill(
          selectedSawmill,
          selectedStackIds,
          (error) => {
            setAssignDialogLoading(false);
            if (error) {
              dispatch(notificationActions.enqueueErrorResponse(error));
            } else {
              dispatch(stackActions.refreshStacks());
              setAssignDialogOpen(false);
            }
          },
        ),
      );
    }
  };

  const handleAssignDialogClose = () => {
    isAssignDialogLoading || setAssignDialogOpen(false);
  };

  function handleDownloadStackArchiveClick() {
    dispatch(stackActions.downloadStackArchive(selectedStackIds));
  }

  function handleDownloadAccumulatedCsvClick() {
    if (measurementCard) {
      dispatch(
        stackActions.downloadAccumulatedCsv(
          measurementCard.id,
          selectedStackIds,
        ),
      );
    }
  }

  async function handleRegenerateFilesClick() {
    if (selectedStackIds.length > 100) {
      dispatch(
        notificationActions.enqueueError(
          t('stacks_page.table.regenerate_files_limit'),
        ),
      );
      return;
    }

    if (selectedStackIds.length > 5) {
      const shouldContinue = window.confirm(
        t('stacks_page.table.regenerate_files_warning', {
          count: selectedStackIds.length,
        }),
      );

      if (!shouldContinue) {
        return;
      }
    }

    setIsRegeneratingFiles(true);
    try {
      await ApiService.bulkRegenerateFiles(selectedStackIds);
      setSelectedStackIds([]);
    } catch (error: any) {
      dispatch(notificationActions.enqueueErrorResponse(error));
    } finally {
      setIsRegeneratingFiles(false);
    }
  }

  const deleteStack = () => {
    if (confirmStackDelete) {
      dispatch(stackActions.deleteStackById(confirmStackDelete.stackId));
      setConfirmStackDelete(undefined);
    }
  };

  const cancelDeleteStack = () => {
    setConfirmStackDelete(undefined);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    if (onPageChanged) {
      onPageChanged(newPage);
    }
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (onPageSizeChanged) {
      onPageSizeChanged(Number(event.target.value));
    }
  };

  // checkbox select logic
  const handleSelectAllClick = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked && stacks) {
      setSelectedStackIds(
        stacks.data
          .filter((stack) => isMeasured(stack.state))
          .map((stack) => stack.stackId),
      );
    } else {
      setSelectedStackIds([]);
    }
  };

  const handleSelectRowClick = (stackId: string) => {
    const selectedIndex = selectedStackIds.indexOf(stackId);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedStackIds, stackId);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedStackIds.slice(1));
    } else if (selectedIndex === selectedStackIds.length - 1) {
      newSelected = newSelected.concat(selectedStackIds.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedStackIds.slice(0, selectedIndex),
        selectedStackIds.slice(selectedIndex + 1),
      );
    }

    setSelectedStackIds(newSelected);
  };

  const isSelected = (stackId: string) =>
    selectedStackIds.indexOf(stackId) !== -1;

  const numSelected = selectedStackIds.length;
  const rowCount =
    stacks?.data.filter((stack) => isMeasured(stack.state)).length ?? 0;

  function stateToString(state: StackStateEnumDto) {
    switch (state) {
      case StackStateEnumDto.ASSIGNED_TO_SAWMILL:
        return t('stacks_page.table.assigned_to_sawmill');
      case StackStateEnumDto.MEASURED:
        return t('stacks_page.table.measured');
      case StackStateEnumDto.PRE_REGISTRATION:
        return t('stacks_page.table.not_measured');
      default:
        return '';
    }
  }

  function toSnakeCase(str: string) {
    // TODO refactor 'preregistration' keys in translation files to match the others
    if (str === 'PreRegistration') return 'preregistration';
    const snakeCase = str.replace(/[A-Z]/g, (match, offset) => {
      if (offset === 0) {
        return match.toLowerCase();
      }
      return `_${match.toLowerCase()}`;
    });
    return snakeCase;
  }

  return (
    <div className={classes.tableLayout}>
      <TableContainer className={classes.container}>
        <Table aria-label="stacks table" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeadCell}>
                <Checkbox
                  className={classes.checkbox}
                  indeterminate={numSelected > 0 && numSelected < rowCount}
                  checked={rowCount > 0 && numSelected === rowCount}
                  onChange={handleSelectAllClick}
                />
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                {t('stacks_page.table.stack_no')}
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                {t('stacks_page.table.stack_id')}
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                {t('stacks_page.table.department')}
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                {t('stacks_page.table.registrator')}
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                {t('stacks_page.table.surveyor')}
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                {t('stacks_page.table.status')}
              </TableCell>
              <TableCell align="right" className={classes.tableHeadCell}>
                {t('stacks_page.date_type.' + toSnakeCase(dateType))}
              </TableCell>
              <TableCell
                align="center"
                className={classes.tableHeadCell}
              ></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stacks?.data?.map((stack: ListStacksResponseItem) => (
              <TableRow
                key={stack.stackId}
                hover={true}
                className={classes.tableRow}
                onClick={() => {
                  history.push(
                    `/organization/${orgId}/department/${stack.departmentId}/stack/${stack.stackId}`,
                  );
                }}
              >
                <TableCell className={classes.textBold}>
                  {isMeasured(stack.state) && (
                    <Checkbox
                      className={classes.checkbox}
                      checked={isSelected(stack.stackId)}
                      onClick={(event: React.MouseEvent) => {
                        event.stopPropagation();
                        handleSelectRowClick(stack.stackId);
                      }}
                    />
                  )}
                </TableCell>

                <TableCell className={classes.tableCell}>
                  {stack.stackNumber}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {stack.polterId ?? ''}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {departments[stack.departmentId] &&
                    departments[stack.departmentId].name}
                </TableCell>
                <TableCell className={classes.textBold}>
                  {stack.registrator}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {stack.surveyor}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  <FiberManualRecord
                    className={classes.measuredIcon}
                    color={isMeasured(stack.state) ? 'primary' : 'error'}
                  />
                  {stateToString(stack.state)}
                </TableCell>
                <TableCell align="right" className={classes.tableCell}>
                  {dateFromSelectedDateType(stack, dateType)}
                </TableCell>
                <TableCell align="center" className={classes.tableCell}>
                  {isPreRegistration(stack.state) && (
                    <IconSpinner
                      isLoading={stacksDeleting.includes(stack.stackId)}
                    >
                      <IconButton
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          setConfirmStackDelete(stack);
                        }}
                        aria-label="delete"
                      >
                        <Delete />
                      </IconButton>
                    </IconSpinner>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {selectedStackIds.length > 0 && (
        <div className={classes.bulkToolbar}>
          <Typography variant="body2" className={classes.bulkToolbarText}>
            {t('stacks_page.table.selected_count', {
              count: selectedStackIds.length,
            })}
          </Typography>
          <Tooltip title={t('stacks_page.dialog.assign_to_sawmill') as string}>
            <span>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                disabled={sawmills.length === 0}
                onClick={() => setAssignDialogOpen(true)}
                startIcon={<SawmillIcon />}
              >
                {t('stacks_page.dialog.assign_to_sawmill')}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={t('stacks_page.table.download_reports') as string}>
            <span>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                disabled={archiveDownloadProgress !== undefined}
                onClick={handleDownloadStackArchiveClick}
                startIcon={
                  <IconSpinner
                    isLoading={!!archiveDownloadProgress}
                    progress={archiveDownloadProgress}
                  >
                    <GetApp />
                  </IconSpinner>
                }
              >
                {t('stacks_page.table.download_reports')}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={t('stacks_page.table.download_stacks') as string}>
            <span>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                disabled={accumulatedCsvDownloadProgress !== undefined}
                onClick={handleDownloadAccumulatedCsvClick}
                startIcon={
                  <IconSpinner
                    isLoading={!!accumulatedCsvDownloadProgress}
                    progress={accumulatedCsvDownloadProgress}
                  >
                    <GetApp />
                  </IconSpinner>
                }
              >
                {t('stacks_page.table.download_stacks')}
              </Button>
            </span>
          </Tooltip>
          {isGlobalAdmin && (
            <Tooltip title={t('stacks_page.table.regenerate_files') as string}>
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  disabled={isRegeneratingFiles}
                  onClick={handleRegenerateFilesClick}
                  startIcon={
                    <IconSpinner isLoading={isRegeneratingFiles}>
                      <Refresh />
                    </IconSpinner>
                  }
                >
                  {t('stacks_page.table.regenerate_files')}
                </Button>
              </span>
            </Tooltip>
          )}
          <Button size="small" onClick={() => setSelectedStackIds([])}>
            {t('common.cancel')}
          </Button>
        </div>
      )}
      <TablePagination
        className={classes.pagination}
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        component="div"
        count={stacks?.totalCount ?? 0}
        rowsPerPage={filters?.pageSize ?? 50}
        page={stacks?.page ? stacks?.page - 1 : 0}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        backIconButtonText={t('pagination.previous_page')}
        labelDisplayedRows={(info: LabelDisplayedRowsArgs) => {
          return `${info.from}-${info.to} ${t('pagination.of')} ${info.count}`;
        }}
        labelRowsPerPage={t('pagination.rows_per_page') + ':'}
        nextIconButtonText={t('pagination.next_page')}
      />
      <Dialog
        open={confirmStackDelete !== undefined}
        onClose={cancelDeleteStack}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t('stacks_page.confirm_delete.title')}
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <DialogContentText id="alert-dialog-description">
            {t('stacks_page.confirm_delete.text', {
              stackNumber: confirmStackDelete?.stackNumber,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDeleteStack} color="primary">
            {t('stacks_page.confirm_delete.cancel')}
          </Button>
          <Button onClick={deleteStack} color="primary" autoFocus>
            {t('stacks_page.confirm_delete.delete')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={isAssignDialogOpen} onClose={handleAssignDialogClose}>
        <OverlaySpinner isLoading={isAssignDialogLoading}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleAssignClicked();
            }}
          >
            <DialogTitle>
              {t('stacks_page.dialog.assign_stacks_to_sawmill')}
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
              <List>
                <RadioGroup
                  name="sawmill"
                  value={selectedSawmill}
                  onChange={handleSawmillRadioChanged}
                >
                  {sawmills.map((sawmill, i) => (
                    <React.Fragment key={sawmill.id}>
                      <ListItem key={sawmill.id}>
                        <ListItemIcon>
                          <div className="sawmill-icon" />
                        </ListItemIcon>
                        <ListItemText>{sawmill.name}</ListItemText>
                        <FormControlLabel
                          value={sawmill.id}
                          control={<Radio />}
                          label=""
                          className={classes.radio}
                        />
                      </ListItem>
                      {i + 1 < sawmills.length && (
                        <Divider variant="inset" component="li" />
                      )}
                    </React.Fragment>
                  ))}
                </RadioGroup>
              </List>
            </DialogContent>
            <DialogActions>
              <Button color="primary" onClick={handleAssignDialogClose}>
                {t('common.cancel')}
              </Button>
              <Button
                color="primary"
                // onClick={handleAssignClicked}
                autoFocus
                type="submit"
              >
                {t('stacks_page.dialog.assign')}
              </Button>
            </DialogActions>
          </form>
        </OverlaySpinner>
      </Dialog>
    </div>
  );
}

export { StacksTable };
